import { useEffect, useMemo, useState } from 'react';
import { ErrorBoundary, type FallbackProps } from 'react-error-boundary';
import { TemplateElement } from './elements/template/TemplateElement';
import { buildElementReactTree } from './elements/buildElementReactTree';
import type { ElementDefinition, RenderContext, RenderRuntime } from './elements/types';

interface TemplateProps {
    structure: ElementDefinition[];
    context: RenderContext;
    runtime: RenderRuntime;
    onReady?: (runtime: RenderRuntime) => void;
    onError?: (error: Error) => void;
}

type TemplateContentProps = Omit<TemplateProps, 'onError'>;

function toError(error: unknown): Error {
    if (error instanceof Error) {
        return error;
    }

    return new Error(String(error));
}

function TemplateContent({
    structure,
    context,
    runtime,
    onReady,
}: TemplateContentProps): JSX.Element {
    const [readyError, setReadyError] = useState<Error | null>(null);
    const rootElement = useMemo(() => new TemplateElement(structure), [structure]);
    const { node, ready } = useMemo(
        () => buildElementReactTree(rootElement, context, runtime),
        [rootElement, context, runtime]
    );

    useEffect(() => {
        setReadyError(null);
        let isActive = true;

        void ready
            .then(() => {
                if (!isActive) {
                    return;
                }

                runtime.finalizeGraphAutoScaling();

                if (onReady) {
                    onReady(runtime);
                }
            })
            .catch((error: unknown) => {
                if (isActive) {
                    setReadyError(toError(error));
                }
            });

        return () => {
            isActive = false;
        };
    }, [ready, runtime, onReady]);

    if (readyError) {
        throw readyError;
    }

    return <>{node}</>;
}

function TemplateErrorFallback({
    error,
    runtime,
    reportsError,
}: FallbackProps & {
    runtime: RenderRuntime;
    reportsError: boolean;
}): JSX.Element {
    const normalizedError = toError(error);

    if (runtime.showEditor) {
        return (
            <div
                role="alert"
                className="rounded border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
            >
                <p className="font-semibold">{gettext('TEMPLATE_RENDER_ERROR')}</p>
                <p className="mt-1 text-xs">{normalizedError.message}</p>
            </div>
        );
    }

    if (reportsError) {
        return <></>;
    }

    throw normalizedError;
}

export function Template({
    structure,
    context,
    runtime,
    onReady,
    onError,
}: TemplateProps): JSX.Element {
    return (
        <ErrorBoundary
            resetKeys={[structure, context, runtime]}
            onError={onError}
            fallbackRender={(fallbackProps) => (
                <TemplateErrorFallback
                    {...fallbackProps}
                    runtime={runtime}
                    reportsError={typeof onError !== 'undefined'}
                />
            )}
        >
            <TemplateContent
                structure={structure}
                context={context}
                runtime={runtime}
                onReady={onReady}
            />
        </ErrorBoundary>
    );
}
